/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import ClickOutsideDetector from "../ClickOutsideDetector";
import { findChildByName } from "../../util/HtmlUtils";
import Button from "../Button";
import { ElipsisVertical } from "../../common/Icons/ElipsisVertical";

interface Props {
  children: ReactElement | ReactElement[];
  className?: string;
  drop?: any;
  isOpen?: boolean;
}

interface ChildProp {
  children?: string | ReactElement | ReactElement[];
  className?: string;
}

type DropdownChildrenTypes = {
  Toggle: React.FC<ChildProp>;
  Content: React.FC<ChildProp>;
};

const Dropdown: React.FunctionComponent<Props> & DropdownChildrenTypes = ({ children, className, isOpen: outsideControlledOpen }: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const childrenArray = React.Children.toArray(children);
  const dropdownContent = findChildByName(childrenArray, "Content") || null;
  const dropdownToggle = findChildByName(childrenArray, "Toggle") || null;

  useEffect(() => {
    if(outsideControlledOpen !== undefined){
      setIsOpen(outsideControlledOpen);
    }
  }, [outsideControlledOpen]);

  return (
    <div>
      <ClickOutsideDetector
        listen
        onClickOutside={() => {
          setIsOpen(false);
        }}
      >
        <div className="relative">
          <div
            role="button"
            onClick={() => {
              setIsOpen((prev) => !prev);
            }}
          >
            {dropdownToggle}
          </div>
          <div
            hidden={!isOpen}
            className={`mt-1 z-40 shadow-md rounded-lg w-46 font-normal bg-white divide-y divide-gray-100 dark:bg-gray-700 dark:divide-gray-600 absolute right-0 ${className}`}
          >
            {dropdownContent}
          </div>
        </div>
      </ClickOutsideDetector>
    </div>
  );
};

const Toggle: React.FC<ChildProp> = ({ children }) => (
  <>
    {children || (
      <Button variant="icon">
        <ElipsisVertical />
      </Button>
    )}
  </>
);
Toggle.displayName = "Toggle";

const Content: React.FC<ChildProp> = ({ children, className }) => <ul className={className || "py-2 text-md"}>{children}</ul>;
Content.displayName = "Content";

Dropdown.Toggle = Toggle;
Dropdown.Content = Content;

export default Dropdown;
